import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { FaFileAlt, FaPlus, FaEdit, FaTrash, FaTimes, FaCheck } from 'react-icons/fa';
import AppDropdown from '../../../components/AppDropdown';
import '../CRM.css';

const TEMPLATES_LIST = ['Welcome', 'Lead Follow-up', 'Quotation', 'Quotation Reminder', 'Payment Reminder', 'Product Information', 'General', 'Promotional'];
const VARIABLES = ['{{customer_name}}', '{{company_name}}', '{{salesperson_name}}', '{{quotation_number}}', '{{quotation_amount}}', '{{followup_date}}'];
const CATEGORY_OPTIONS = TEMPLATES_LIST.map((c) => ({ value: c, label: c }));
const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English (en)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'mr', label: 'Marathi (mr)' },
];
const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
  { value: 'Draft', label: 'Draft' },
];

export const TemplateManager = ({ templates: initial = [], loading = false, onSave, onDelete }) => {
  const [templates, setTemplates] = useState(initial);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'General', language: 'en', content: '', status: 'Active' });

  useEffect(() => { setTemplates(initial); }, [initial]);

  const openCreate = () => { setEditing('new'); setForm({ name: '', category: 'General', language: 'en', content: '', status: 'Active' }); };
  const openEdit = (t) => { setEditing(t.id); setForm({ ...t }); };

  const insertVar = (v) => setForm((p) => ({ ...p, content: p.content + v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Enter a template name.'); return; }
    if (!form.content.trim()) { toast.error('Template content is required.'); return; }
    const saved = { ...form, id: editing === 'new' ? Date.now() : editing };
    try {
      await onSave?.(saved);
    } catch {
      toast.error('Failed to save template.');
      return;
    }
    setTemplates((p) => editing === 'new' ? [...p, saved] : p.map((t) => t.id === editing ? saved : t));
    setEditing(null);
  };

  const remove = async (id) => {
    const t = templates.find((x) => x.id === id);
    if (!window.confirm(`Delete template "${t?.name || ''}"?`)) return;
    setTemplates((p) => p.filter((t2) => t2.id !== id));
    try {
      await onDelete?.(id);
    } catch {
      toast.error('Failed to delete template.');
    }
  };

  return (
    <div>
      <div className="crm-header" style={{ marginBottom: 18 }}>
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaFileAlt aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">WhatsApp Templates</h1>
            <p className="crm-subtitle">Manage message templates for bulk messaging and follow-ups</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button type="button" className="crm-btn crm-btn-primary" onClick={openCreate}><FaPlus aria-hidden="true" /> New Template</button>
        </div>
      </div>

      <div className="crm-list-grid">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div className="crm-list-card" key={`skeleton-${i}`}>
              <div className="crm-skeleton" style={{ height: 18, width: '55%', borderRadius: 6, marginBottom: 10 }} />
              <div className="crm-skeleton" style={{ height: 12, width: '40%', borderRadius: 6, marginBottom: 14 }} />
              <div className="crm-skeleton" style={{ height: 12, width: '100%', borderRadius: 6, marginBottom: 6 }} />
              <div className="crm-skeleton" style={{ height: 12, width: '90%', borderRadius: 6, marginBottom: 6 }} />
              <div className="crm-skeleton" style={{ height: 12, width: '70%', borderRadius: 6 }} />
            </div>
          ))
        ) : (
          <>
            {templates.map((t) => (
              <div className="crm-list-card" key={t.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="crm-list-card-title">{t.name}</div>
                    <div className="crm-list-card-sub">{t.category} · {t.language?.toUpperCase()}</div>
                  </div>
                  <span className={`crm-badge ${t.status === 'Active' ? 'crm-badge-won' : 'crm-badge-lost'}`}>{t.status}</span>
                </div>
                <div style={{ fontSize: '0.79rem', color: 'var(--ink-soft)', marginTop: 10, whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'hidden' }}>
                  {t.content}
                </div>
                <div className="crm-list-card-footer">
                  <span style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>
                    {(t.content.match(/\{\{\w+\}\}/g) || []).length} variables
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="crm-btn crm-btn-sm crm-btn-outline" onClick={() => openEdit(t)}><FaEdit aria-hidden="true" /> Edit</button>
                    <button type="button" className="crm-btn crm-btn-sm crm-btn-danger" onClick={() => remove(t.id)}><FaTrash aria-hidden="true" /> Delete</button>
                  </div>
                </div>
              </div>
            ))}
            {!templates.length && (
              <div className="crm-empty" style={{ gridColumn: '1/-1' }}>
                <div className="crm-empty-icon"><FaFileAlt aria-hidden="true" /></div>
                <div className="crm-empty-title">No templates yet</div>
                <div className="crm-empty-desc">Create your first WhatsApp template to get started.</div>
              </div>
            )}
          </>
        )}
      </div>

      {editing && createPortal(
        <div className="crm-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="crm-modal crm-modal-lg" role="dialog" aria-modal="true" aria-label={editing === 'new' ? 'New template' : 'Edit template'}>
            <div className="crm-modal-header">
              <h3><FaFileAlt aria-hidden="true" /> {editing === 'new' ? 'New Template' : 'Edit Template'}</h3>
              <button type="button" className="crm-modal-close" onClick={() => setEditing(null)} aria-label="Close dialog"><FaTimes aria-hidden="true" /></button>
            </div>
            <div className="crm-modal-body">
              <div className="crm-form-grid">
                <div className="crm-field">
                  <label>Template Name <span className="req">*</span></label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Quotation Reminder" />
                </div>
                <div className="crm-field">
                  <label>Category</label>
                  <AppDropdown value={form.category} options={CATEGORY_OPTIONS}
                    onChange={(v) => setForm((p) => ({ ...p, category: v }))}
                    menuWidth={220} menuClassName="crm-dropdown-window" />
                </div>
                <div className="crm-field">
                  <label>Language</label>
                  <AppDropdown value={form.language} options={LANGUAGE_OPTIONS}
                    onChange={(v) => setForm((p) => ({ ...p, language: v }))}
                    menuWidth={200} menuClassName="crm-dropdown-window" />
                </div>
                <div className="crm-field">
                  <label>Status</label>
                  <AppDropdown value={form.status} options={STATUS_OPTIONS}
                    onChange={(v) => setForm((p) => ({ ...p, status: v }))}
                    menuWidth={180} menuClassName="crm-dropdown-window" />
                </div>
                <div className="crm-field crm-field-full">
                  <label>Content <span className="req">*</span></label>
                  <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                    placeholder="Dear {{customer_name}}, ..." style={{ minHeight: 130 }} />
                </div>
                <div className="crm-field crm-field-full">
                  <label>Insert Variable</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {VARIABLES.map((v) => (
                      <button key={v} className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => insertVar(v)}
                        style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="crm-modal-footer">
              <button type="button" className="crm-btn crm-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" className="crm-btn crm-btn-primary" onClick={save}><FaCheck aria-hidden="true" /> Save Template</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TemplateManager;
